import { Handle, Position } from "@xyflow/react";
import { NodeTypes, type NodeData } from "@/workflow_api/types/workflow";
import { useWorkflowStore } from "@/workflow_api/store/workflowStore";
import { NodeContent } from "./content";
import MultiConnections from "./connections/multi-connections";
import MultiInputs from "./connections/multi-inputs";
import MultiMergeInputs from "./connections/multi-merge-inputs";
import Connection from "./connections";
import { useUIStore } from "@/workflow_api/store/uiStore";
import ConnectionsWithExpiration from "./connections/with-expiration";
import { cn } from "@/utils/theme";
import React, { useEffect } from "react";
import { useSyncInteroperabilityOutputs } from "@/workflow/components/configurations/interoperability/hooks/useSyncInteroperabilityOutputs";
import { Icon } from "@elaniintc/goes-design-system";

interface WorkflowNodeProps {
  id: string;
  data: NodeData;
  selected?: boolean;
  dragging?: boolean;
}

export function WorkflowNode({ data, id, dragging }: WorkflowNodeProps) {
  const {
    nodeConnecting,
    selectedNodes,
    deletingNode,
    setSelectedNodeId,
    getNodeById,
    setLastClickPosition,
    edges,
  } = useWorkflowStore();
  const {
    selectedNodeId,
    setConnectionToSplit,
    setNodeCreatorOpen,
    nodesWithErrors,
    setModalState,
    setSelectedErrors,
  } = useUIStore();
  const error = nodesWithErrors.get(id);
  const hasError = !!error;
  const isNodeValid = nodesWithErrors.size > 0 && !hasError;
  const isConnecting = nodeConnecting === id;
  const isSelected = selectedNodeId === id || selectedNodes.includes(id);
  const isDeleting = deletingNode === id;

  // Always call hooks unconditionally - React Hooks rules
  useSyncInteroperabilityOutputs(id);

  const mergeInputs = React.useMemo(() => {
    if (data.type !== NodeTypes.MERGE) return null;

    return (
      data.inputs
        ?.filter((input) => input.type === "MERGE_ENTRY")
        .map((entry, index) => {
          const entryValue =
            typeof entry.value === "string"
              ? entry.value
                ? JSON.parse(entry.value)
                : {}
              : entry.value || {};

          const handleId = `merge-input-${index + 1}`;
          const hasConnection = edges.some(
            (edge) => edge.target === id && edge.targetHandle === handleId,
          );

          return {
            id: handleId,
            name: entryValue?.name || `Entrada ${index + 1}`,
            node_id: entryValue?.node_id || "",
            hasConnection,
          };
        }) || []
    );
  }, [data.inputs, data.type, edges, id]);

  const handleNodeClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedNodeId(id);
  };

  const handleNodeDoubleClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedNodeId(id);
    setModalState("details");
  };

  const handleAddNode = (event: React.MouseEvent, outputId: string) => {
    event.stopPropagation();

    // Get current node's position
    const currentNode = getNodeById(id);
    const currentPosition = currentNode?.position || { x: 0, y: 0 };

    // Set position for new node (offset to the right of the current node)
    setLastClickPosition({
      x: currentPosition.x + 200, // 200px to the right of current node
      y: currentPosition.y, // Same vertical position
    });

    // Store the source handle info for auto-connection
    setConnectionToSplit({
      edgeId: "", // Not splitting, creating new connection
      sourceNodeId: id,
      targetNodeId: "", // Will be the new node
      sourceHandle: outputId,
      targetHandle: "main", // Default target handle
    });

    // Open node creator
    setNodeCreatorOpen(true);
  };

  // const getNodeStatusColor = () => {
  //   switch (data.execution.status) {
  //     case "success":
  //       return "#10b981"; // green-500
  //     case "error":
  //       return "#ef4444"; // red-500
  //     case "running":
  //       return "#f59e0b"; // amber-500
  //     case "waiting":
  //       return "#6366f1"; // indigo-500
  //     default:
  //       return "#6b7280"; // gray-500
  //   }
  // };

  const isNodeInitializer = data.type === "initializer-node";

  const getNodeSize = () => {
    let height = 120; // Default height for most nodes

    if (data.type === NodeTypes.WAIT) {
      const threadInputs =
        data.inputs?.filter((input) => input.type === "THREAD") || [];
      const inputsCount = threadInputs.length;
      if (inputsCount <= 3) {
        height = 120; // Default height for 3 or fewer inputs
      } else if (inputsCount > 3) {
        height = 120 + (inputsCount - 2) * 40; // 40px for each additional input beyond 2
      }
    }
    // Other multi-connection nodes use connections for height calculation
    else if (
      data.type === NodeTypes.SWITCH ||
      data.type === NodeTypes.MULTITHREAD ||
      data.type === NodeTypes.WAIT ||
      data.type === NodeTypes.INTEROPERABILITY ||
      data.type === NodeTypes.MERGE
    ) {
      const connectionsLength =
        data.type === NodeTypes.MERGE
          ? mergeInputs?.length || 0
          : Object.keys(data.connections || {}).length;

      if (connectionsLength <= 3) {
        height = 120;
      } else if (connectionsLength > 3) {
        height = 120 + (connectionsLength - 2) * 40;
      }
    }
    return {
      width: 120,
      height,
    };
  };

  useEffect(() => {
    if (!error || error.length === 0) {
      setModalState("idle");
      return;
    }
  }, [error, setModalState]);

  return (
    <div className="relative">
      {error && (
        <button
          type="button"
          className="z-10 absolute -right-4 -top-4 border bg-surface-danger-solid rounded-full size-7 text-on-solid border-none flex items-center justify-center hover:cursor-pointer no-drag"
          onClick={() => {
            setSelectedErrors(error);
            setModalState("error");
          }}
        >
          <Icon type="fas" name="info-circle" />
        </button>
      )}
      {isNodeValid && (
        <div className="z-10 absolute -right-4 -top-4 border bg-surface-sucess-solid rounded-full size-7 text-on-solid border-none flex items-center justify-center no-drag">
          <Icon type="fas" name="check" />
        </div>
      )}
      {isDeleting && (
        <div className="z-10 absolute -right-4 -top-4 border bg-surface-danger-solid rounded-full size-7 text-on-solid border-none flex items-center justify-center no-drag">
          <Icon type="fa" name="circle-info" />
        </div>
      )}
      <div
        className={`
        workflow-node
        ${isSelected ? "selected" : ""}
        ${dragging ? "dragging" : ""}
        ${isDeleting || hasError ? "deleting" : ""}
        ${isNodeValid ? "valid" : ""}
        `}
        // ${data.disabled ? "disabled" : ""}
        // ${data.execution.status || "new"}
        // ${data.render.type}
        style={{
          width: getNodeSize().width,
          height: getNodeSize().height,
        }}
        onClick={handleNodeClick}
        onDoubleClick={handleNodeDoubleClick}
      >
        {/* Input handles */}
        {!isNodeInitializer &&
          data.type !== NodeTypes.WAIT &&
          data.type !== NodeTypes.MERGE && (
            <div
              style={{
                position: "absolute",
                top: "51%",
                left: -20,
                transform: "translateY(-50%)",
                width: "20px",
                height: "14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: "8px",
              }}
            >
              <Handle
                type="target"
                id="target"
                position={Position.Left}
                className="!rounded-none !border-none !bg-transparent z-40 !left-0 no-drag"
                style={{
                  position: "relative",
                  top: "50%",
                  ...(data.edge
                    ? {
                        transform: "translateY(-50%)",
                        width: "20px",
                        pointerEvents: "none",
                      }
                    : {
                        transform: "translateY(-50%) translateX(0%)",
                      }),
                }}
              >
                <div
                  className={cn(
                    "w-[3px] h-3.5 bg-on-disabled rounded-l-full absolute right-px pointer-events-none handle-indicator",
                    isSelected && "bg-action-quiet",
                    (isDeleting || hasError) && "bg-danger-quiet",
                    isNodeValid && "bg-success-quiet",
                  )}
                ></div>
                {data.edge && (
                  <div
                    className={cn(
                      "w-[20px] h-px border border-neutral absolute top-1.5 -z-10 -left-0.5 pointer-events-none",
                      isSelected && "border-action-quiet",
                      isNodeValid && "border-success-quiet",
                    )}
                  ></div>
                )}
              </Handle>
            </div>
          )}
        {/* Multi inputs for Wait nodes */}
        {data.type === NodeTypes.WAIT && data.inputs && (
          <MultiInputs
            node_id={id}
            inputs={
              // Convertir inputs THREAD al formato simplificado
              data.inputs
                .filter((input) => input.type === "THREAD")
                .map((input, index) => ({
                  id: input.id,
                  name: input.name || `Entrada ${index + 1}`,
                  hasConnection:
                    Array.isArray(input.source_connections) &&
                    input.source_connections.length > 0,
                }))
            }
            isSelected={isSelected}
            isDeleting={isDeleting}
            hasError={hasError}
            isValid={isNodeValid}
          />
        )}
        {/* Multi inputs for Merge nodes */}
        {data.type === NodeTypes.MERGE && mergeInputs && (
          <MultiMergeInputs
            node_id={id}
            inputs={mergeInputs}
            isSelected={isSelected}
            isDeleting={isDeleting}
            hasError={hasError}
            isValid={isNodeValid}
          />
        )}
        <NodeContent displayName={data.displayName} id={id} type={data.type} />
        {data.type !== NodeTypes.END && (
          <>
            {data.type === NodeTypes.IF ||
            data.type === NodeTypes.SWITCH ||
            data.type === NodeTypes.MULTITHREAD ||
            data.type === NodeTypes.INTEROPERABILITY ||
            (data.connections && Object.keys(data.connections).length > 1) ? (
              <MultiConnections
                node_id={id}
                connections={data.connections || {}}
                isConnecting={isConnecting}
                onAdd={handleAddNode}
                isSelected={isSelected}
                isDeleting={isDeleting}
                hasError={hasError}
                isValid={isNodeValid}
              />
            ) : data.connections?.["expired"] ? (
              <ConnectionsWithExpiration
                id={id}
                isConnecting={isConnecting}
                onAdd={handleAddNode}
                connections={data.connections}
                isSelected={isSelected}
                isDeleting={isDeleting}
                hasError={hasError}
                isValid={isNodeValid}
              />
            ) : (
              <Connection
                id={id}
                isConnecting={isConnecting}
                onAdd={handleAddNode}
                isSelected={isSelected}
                isDeleting={isDeleting}
                hasError={hasError}
                isValid={isNodeValid}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
